/**
 * transport/attach.ts —— 附件上行（**同一 WS 信封**；CONTRACT §2 上行「附件族」，零第二通道）。
 *
 * 依据（组件侧受理面逐字对齐，禁发明键/形）：
 *  · 帧键面＝`components/entry_session/file_ingress.py::FILE_KEYS` 的**子集**
 *    `{kind,name,content_type,size,data}`（`data`＝base64；后端 `validate=True` 解，非法即拒）；
 *  · `kind ∈ {attachment, audio.chunk}`（`FILE_KINDS`）；音频分片另需 `run_ref/chunk_seq`（本件不做）；
 *  · 回执＝CONTRACT §3 第三帧型（键闭集 `{id,kind,status,reply,event,error}`），附件回执
 *    `kind="attachment"`，成功时 `reply.file.file_id`（32 位 hex）。
 *
 * 配对纪律（与 `command_ack` 的 outbox 同口径）：后端回执**不含请求 id** ⇒ 按**到达序 FIFO** 配对；
 * 无等待者的附件回执 ⇒ 吞掉（**不入投影**——outbox 不造行，左/中栏不受上传影响）。
 */
import type { WsClient } from "./wsClient";

/** 附件上行帧（键面＝后端 FILE_KEYS 子集；多一键即越键被拒） */
export interface AttachmentFrame {
  kind: "attachment";
  name: string;
  content_type: string;
  size: number;
  /** base64（RFC 4648 标准表；后端 `base64.b64decode(validate=True)`） */
  data: string;
}

/** 附件回执里的登记记录（后端 `_public(record)` 投影：只出登记字段，不出路径） */
export interface AttachRecord {
  file_id: string;
  family?: string;
  sha256?: string;
  size?: number;
  owner?: string;
  retention?: string;
  gen_no?: number;
}

/** 附件回执帧（CONTRACT §3 键闭集） */
export interface AttachmentAckFrame {
  id?: string;
  kind?: string;
  status?: string;
  reply?: { accepted?: boolean; kind?: string; file?: AttachRecord; idem_key?: string | null };
  event?: unknown;
  error?: { code?: string; message?: string };
}

/** 字节 → base64（分块，避免大文件 `String.fromCharCode(...)` 栈溢出） */
export function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let bin = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)) as unknown as number[]);
  }
  return btoa(bin);
}

/** 构造附件上行帧（空内容 ⇒ 空 `data`＝后端显式拒 E_FILES_EMPTY，不在此静默丢弃） */
export function buildAttachment(name: string, contentType: string, bytes: Uint8Array): AttachmentFrame {
  return {
    kind: "attachment",
    name,
    content_type: contentType || "application/octet-stream",
    size: bytes.length,
    data: bytesToBase64(bytes),
  };
}

/** 经 WS 上行发送附件帧；未连 ⇒ false（调用方显式处理，不假发） */
export function sendAttachment(client: WsClient, frame: AttachmentFrame): boolean {
  return client.send(frame as unknown as Record<string, unknown>);
}

/** 帧判别：本件认领的附件回执？（`kind="attachment"`；无等待者时由调用方决定是否吞掉） */
export function isAttachmentAck(frame: unknown): frame is AttachmentAckFrame {
  const f = frame as AttachmentAckFrame | null;
  return !!f && typeof f === "object" && f.kind === "attachment";
}

export interface AttachQueue {
  /** 挂一个等待者（返回 promise；`timeoutMs` 到点 ⇒ 显式 reject，防悬挂） */
  wait: (timeoutMs?: number) => Promise<AttachRecord>;
  /** 回执到达：配给**最老**的等待者；无等待者 ⇒ 返回 false（吞掉，不入投影） */
  settle: (frame: AttachmentAckFrame) => boolean;
  /** 撤销一个等待者（abort/未连）：按顺序把错的挂起项摘掉 */
  abort: (reason?: string) => void;
  size: () => number;
}

/** FIFO 等待队列（后端回执不含请求 id ⇒ 到达序配对；与 outbox 同口径） */
export function createAttachQueue(): AttachQueue {
  type Pending = {
    resolve: (r: AttachRecord) => void;
    reject: (e: Error) => void;
    timer: ReturnType<typeof setTimeout> | null;
  };
  const items: Pending[] = [];
  const drop = (p: Pending): void => {
    const i = items.indexOf(p);
    if (i >= 0) items.splice(i, 1);
    if (p.timer) clearTimeout(p.timer);
  };
  return {
    wait(timeoutMs = 120000) {
      return new Promise<AttachRecord>((resolve, reject) => {
        const p: Pending = { resolve, reject, timer: null };
        p.timer = setTimeout(() => {
          drop(p);
          reject(new Error("附件上行超时（未收到回执）"));
        }, timeoutMs);
        items.push(p);
      });
    },
    settle(frame) {
      const p = items.shift();
      if (!p) return false;                       // 无等待者 ⇒ 吞掉（不造行、不报错）
      if (p.timer) clearTimeout(p.timer);
      const file = frame.reply && frame.reply.file;
      if (frame.status === "accepted" && file && file.file_id) p.resolve(file);
      else {
        const err = frame.error || {};
        p.reject(new Error(err.message || err.code || `附件被拒（status=${frame.status}）`));
      }
      return true;
    },
    abort(reason = "已撤销") {
      const p = items.shift();
      if (p) { if (p.timer) clearTimeout(p.timer); p.reject(new Error(reason)); }
    },
    size: () => items.length,
  };
}
